import React, { createContext, useContext, useState, type HTMLAttributes } from 'react';
import { ChevronDown } from '@core/icons-react';
import { cn } from '../../../utils/classnames';

type CollapsibleContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const CollapsibleContext = createContext<CollapsibleContextValue | null>(null);

const useCollapsible = () => {
  const context = useContext(CollapsibleContext);
  if (!context) {
    throw new Error('Collapsible components must be used within Collapsible');
  }
  return context;
};

export type CollapsibleProps = HTMLAttributes<HTMLDivElement> & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  asChild?: boolean;
};

export const Collapsible = ({
  className,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  children,
  asChild = false,
  ...props
}: CollapsibleProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  
  const setOpen = (newOpen: boolean) => {
    if (controlledOpen === undefined) {
      setUncontrolledOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const value = { open, setOpen };

  if (asChild && React.isValidElement(children)) {
    return (
      <CollapsibleContext.Provider value={value}>
        {React.cloneElement(children, {
          ...(children.props as any),
          ...props,
          'data-state': open ? 'open' : 'closed',
        } as any)}
      </CollapsibleContext.Provider>
    );
  }

  return (
    <CollapsibleContext.Provider value={value}>
      <div
        className={className}
        data-state={open ? 'open' : 'closed'}
        {...props}
      >
        {children}
      </div>
    </CollapsibleContext.Provider>
  );
};

export type CollapsibleTriggerProps = HTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
};

export const CollapsibleTrigger = ({
  className,
  onClick,
  asChild = false,
  children,
  ...props
}: CollapsibleTriggerProps) => {
  const { open, setOpen } = useCollapsible();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(!open);
    onClick?.(e);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...(children.props as any),
      onClick: handleClick,
      'data-state': open ? 'open' : 'closed',
      'aria-expanded': open,
    } as any);
  }

  return (
    <button
      className={className}
      data-state={open ? 'open' : 'closed'}
      aria-expanded={open}
      type="button"
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
};

export type CollapsibleContentProps = HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
};

export const CollapsibleContent = ({
  className,
  asChild = false,
  children,
  ...props
}: CollapsibleContentProps) => {
  const { open } = useCollapsible();

  if (!open) {
    return null;
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...(children.props as any),
      ...props,
      'data-state': open ? 'open' : 'closed',
    } as any);
  }

  return (
    <div
      className={className}
      data-state={open ? 'open' : 'closed'}
      {...props}
    >
      {children}
    </div>
  );
};

Collapsible.displayName = 'Collapsible';
CollapsibleTrigger.displayName = 'CollapsibleTrigger';
CollapsibleContent.displayName = 'CollapsibleContent';

